import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Wheat, MapPin, ArrowRight, Send, Landmark } from "lucide-react";

const nationalStats = [
  { value: "1,200+", label: "CDBG entitlement communities nationwide", source: "HUD" },
  { value: "600+", label: "U.S. counties classified as persistent- or high-poverty", source: "USDA ERS" },
  { value: "10M+", label: "Americans 65+ aging in housing not designed for them", source: "HUD / Census" },
  { value: "$60B+", label: "Federal benefits left unclaimed by eligible Americans each year", source: "GAO / USDA / SSA" },
];

const urbanRural = [
  {
    icon: Building2,
    label: "Urban Deployment Shape",
    points: [
      "Dense partner network: community colleges, regional hospitals, housing authorities, transit",
      "Service Connector caseload: 1:38, walking radius",
      "Multigenerational household + voucher + Section 8 portfolios",
      "Concentrated population in HUD CDBG entitlement tracts",
    ],
  },
  {
    icon: Wheat,
    label: "Rural Deployment Shape",
    points: [
      "Distributed partner network: rural CC, telehealth, USDA Rural Development, regional EMS",
      "Service Connector caseload: 1:38, windshield-time-aware",
      "General community portfolio with home-mod-heavy senior track",
      "Persistent-poverty county targeting with USDA + HUD pooled funding",
    ],
  },
];

export default function NationalFootprint() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    role: "",
    org: "",
    community: "",
    email: "",
    notes: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: `[BRING BRIDGE INQUIRY]\nRole: ${form.role}\nOrganization: ${form.org}\nCommunity: ${form.community}\nNotes: ${form.notes}`,
        }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true); // gracefully degrade — never block on form errors
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      {/* ============================ NATIONAL STATISTICS STRIP ============================ */}
      <section
        id="national"
        data-testid="home-national-stats"
        className="bg-[#1F2937] text-[#FDF8EF] py-16 lg:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F4D77A] mb-8 text-center">
            The National Footprint · Underserved America by the Numbers
          </div>
          <ul className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#FDF8EF]/15 border border-[#FDF8EF]/15">
            {nationalStats.map((s, i) => (
              <li
                key={s.label}
                data-testid={`national-stat-${i}`}
                className="bg-[#1F2937] p-6 lg:p-7"
              >
                <div className="font-display text-3xl lg:text-4xl text-[#F4D77A] leading-none mb-3">{s.value}</div>
                <p className="text-xs lg:text-sm text-[#D1D5DB] leading-relaxed mb-2">{s.label}</p>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#9CA3AF]">Source · {s.source}</div>
              </li>
            ))}
          </ul>
          <p className="text-center font-display italic text-base lg:text-lg text-[#FDF8EF] mt-8 max-w-3xl mx-auto leading-relaxed">
            One in eight Americans lives in a community where the resources exist and the chain does not.
            <span className="text-[#F4D77A]"> Bridge is the chain.</span>
          </p>
        </div>
      </section>

      {/* ============================ URBAN + RURAL DUALITY ============================ */}
      <section
        id="urban-rural"
        data-testid="home-urban-rural"
        className="bg-[#FDF8EF] py-20 lg:py-28 border-y border-[#E8DCC3]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-3">
              One Methodology · Two Deployment Shapes
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight">
              Inner-city. <em className="italic text-[#B8860B]">Rural county.</em><br />
              Same chain. Same dignity.
            </h2>
          </div>
          <ul className="grid md:grid-cols-2 gap-6">
            {urbanRural.map((u, i) => {
              const Icon = u.icon;
              return (
                <li
                  key={u.label}
                  data-testid={`urban-rural-${i}`}
                  className="bg-white border border-[#E8DCC3] p-7 lg:p-8"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center bg-[#F4D77A]/30 mb-5">
                    <Icon className="h-6 w-6 text-[#B8860B]" />
                  </div>
                  <h3 className="font-display text-xl lg:text-2xl text-[#1F2937] mb-4">{u.label}</h3>
                  <ul className="space-y-2.5">
                    {u.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-[#4B5563] leading-relaxed">
                        <span className="text-[#B8860B] shrink-0 mt-0.5">▸</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ============================ FEDERAL COORDINATION PANEL ============================ */}
      <section
        id="federal-coordination"
        data-testid="home-federal-coordination"
        className="bg-[#1F2937] text-[#FDF8EF] py-16 lg:py-20"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <Landmark className="h-14 w-14 text-[#F4D77A]" />
            </div>
            <div className="lg:col-span-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F4D77A] mb-3">
                Federal Coordination · In Progress
              </div>
              <h3 className="font-display text-2xl lg:text-3xl leading-snug mb-4">
                The Bridge Model is being presented to the White House and to HUD Secretary Scott Turner's office.
              </h3>
              <p className="text-sm lg:text-base text-[#D1D5DB] leading-relaxed mb-5 max-w-3xl">
                A formal pitch packet has been delivered for federal endorsement,
                CDBG technical-assistance designation, and Opportunity-Zone
                coordination. Sterile Mate LLC is SAM.gov registered (CAGE 9GH66 ·
                All Awards). The methodology is built to be replicated across
                underserved urban and rural communities under one fidelity
                standard.
              </p>
              <Link
                to="/baytown#federal"
                data-testid="home-federal-link"
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#F4D77A] hover:text-white"
              >
                See the federal strategy <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ BRING BRIDGE TO YOUR COMMUNITY ============================ */}
      <section
        id="bring-bridge"
        data-testid="home-bring-bridge"
        className="bg-gradient-to-b from-[#FDF8EF] to-[#F4EBD8] py-20 lg:py-28"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-3">
                Open to Applications · Nationwide
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight mb-5">
                Bring Bridge to your <em className="italic text-[#B8860B]">community.</em>
              </h2>
              <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-6">
                Municipal leaders, foundation officers, congressional staff, and
                federal program officers — request a discovery briefing. We will
                walk you through a customized deployment shape for your city,
                county, or region, urban or rural, within two weeks of inquiry.
              </p>
              <ul className="space-y-2 text-sm text-[#4B5563]">
                <li className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-[#B8860B] mt-1 shrink-0" /> Urban · CDBG entitlement deployment</li>
                <li className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-[#B8860B] mt-1 shrink-0" /> Rural · USDA + HUD pooled deployment</li>
                <li className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-[#B8860B] mt-1 shrink-0" /> Tribal nation · sovereign-government coordination</li>
                <li className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-[#B8860B] mt-1 shrink-0" /> Foundation-funded pilot · 12-month flagship</li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              {submitted ? (
                <div data-testid="bring-bridge-success" className="bg-white border-2 border-[#B8860B] p-8 lg:p-10 text-center">
                  <div className="font-display text-2xl text-[#1F2937] mb-3">Thank you.</div>
                  <p className="text-[#4B5563] leading-relaxed">
                    Your inquiry has reached George directly. You will hear back
                    within 5 business days with a custom deployment briefing for
                    your community.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="bring-bridge-form" className="bg-white border border-[#E8DCC3] p-7 lg:p-9 shadow-md">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Your name</span>
                      <input data-testid="bb-name" required value={form.name} onChange={upd("name")} className="mt-1 w-full border-b border-[#E8DCC3] bg-transparent py-2 focus:border-[#B8860B] focus:outline-none text-[#1F2937]" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Role / title</span>
                      <input data-testid="bb-role" required value={form.role} onChange={upd("role")} className="mt-1 w-full border-b border-[#E8DCC3] bg-transparent py-2 focus:border-[#B8860B] focus:outline-none text-[#1F2937]" />
                    </label>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Organization</span>
                      <input data-testid="bb-org" required value={form.org} onChange={upd("org")} className="mt-1 w-full border-b border-[#E8DCC3] bg-transparent py-2 focus:border-[#B8860B] focus:outline-none text-[#1F2937]" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Community (city, county, region)</span>
                      <input data-testid="bb-community" required value={form.community} onChange={upd("community")} className="mt-1 w-full border-b border-[#E8DCC3] bg-transparent py-2 focus:border-[#B8860B] focus:outline-none text-[#1F2937]" />
                    </label>
                  </div>
                  <label className="block mb-4">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">Email</span>
                    <input data-testid="bb-email" type="email" required value={form.email} onChange={upd("email")} className="mt-1 w-full border-b border-[#E8DCC3] bg-transparent py-2 focus:border-[#B8860B] focus:outline-none text-[#1F2937]" />
                  </label>
                  <label className="block mb-6">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">What you're trying to solve (optional)</span>
                    <textarea data-testid="bb-notes" rows={3} value={form.notes} onChange={upd("notes")} className="mt-1 w-full border-b border-[#E8DCC3] bg-transparent py-2 focus:border-[#B8860B] focus:outline-none text-[#1F2937] resize-none" />
                  </label>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="bb-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#1F2937] hover:bg-[#0A192F] text-[#FDF8EF] h-12 px-6 font-mono text-xs uppercase tracking-widest transition-colors disabled:opacity-60"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {submitting ? "Sending…" : "Request a discovery briefing"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
